import React, { memo, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import { FiEdit2, FiFilePlus } from 'react-icons/fi';
import { EDIT_PEN, ADD_SURVEY } from 'assets/icons';
import { Img, Checkbox } from 'ui-kit';
import { handleEnterPress } from 'utils/custom';
// Imported by path, not through the hooks barrel: that barrel pulls hooks which import ui-kit, and
// ui-kit importing it back would close the cycle.
import useNewUi from 'hooks/useNewUi';
import { PERMISSIONS, USER_ROLE_TYPES } from '_constants';
import { AccessControl, IsAzAccount } from 'modules';
import * as Styled from './styles';

/**
 * The row actions in the new design are react-icons drawing in `currentColor`, so they take the
 * cell's forest colour and can be hovered/disabled like any other icon button. The legacy UI keeps
 * its <img> icons — those are SVG files with `fill="#0D0D0D"` baked in, which no amount of CSS on
 * the <img> can recolour.
 */
const ActionIcon = ({ isNewUi, Icon, legacySrc, label, onActivate }) => {
  const shared = {
    tabIndex: 0,
    id: uniqueId(),
    onKeyDown: (e) => handleEnterPress(e, onActivate),
    onClick: onActivate,
  };
  if (isNewUi)
    return (
      <Styled.IconButton {...shared} role="button" aria-label={label}>
        <Icon />
      </Styled.IconButton>
    );
  // Legacy: the same <img> with the same props it has always had — not even an extra attribute.
  return <Img {...shared} size={[22, 22]} src={legacySrc} alt={label} />;
};

const { ACCOUNT_MANAGER, MULTI_ACCOUNT_MANAGER, AGENCY_MANAGER, CAMPAIGN_MANAGER, DATA_COLLECTOR } = USER_ROLE_TYPES;

const ActionCell = ({
  original,
  route,
  editText,
  suspensionAction,
  usersAction,
  agenciesAction,
  setOpenSurveyModal,
  onlyEdit,
  isCheckBox,
  name,
  value,
  handleCheckbox,
}) => {
  const history = useHistory();
  const isAzAccount = IsAzAccount();

  const path = useMemo(() => (route ? `${route}/${original?.id}` : ''), [original, route]);

  const editAccess = useMemo(() => {
    switch (editText) {
      case 'enforcement':
      case 'education':
      case 'assessment': {
        return {
          permission: PERMISSIONS.CHANGE_VISIT,
          ruleSet: {
            assigned: [DATA_COLLECTOR],
            agency: [AGENCY_MANAGER],
            agencySecGeogs: [CAMPAIGN_MANAGER],
            all: [ACCOUNT_MANAGER, MULTI_ACCOUNT_MANAGER],
          },
        };
      }
      case 'retailer': {
        return {
          permission: PERMISSIONS.CHANGE_RETAILER,
          ruleSet: {
            secGeogs: [DATA_COLLECTOR, AGENCY_MANAGER, CAMPAIGN_MANAGER, ACCOUNT_MANAGER],
            all: [MULTI_ACCOUNT_MANAGER],
          },
        };
      }
      case 'youth': {
        return {
          permission: PERMISSIONS.CHANGE_YOUTH,
          ruleSet: isAzAccount
            ? {}
            : {
                agencySecGeogs: [ACCOUNT_MANAGER, AGENCY_MANAGER, CAMPAIGN_MANAGER],
                all: [MULTI_ACCOUNT_MANAGER],
              },
        };
      }
      case 'edit venues': {
        return {
          permission: PERMISSIONS.CHANGE_VENUE,
          ruleSet: {
            secGeogs: [DATA_COLLECTOR, AGENCY_MANAGER, CAMPAIGN_MANAGER, ACCOUNT_MANAGER],
            all: [MULTI_ACCOUNT_MANAGER],
          },
        };
      }
      case 'edit user': {
        return {
          permission: PERMISSIONS.CHANGE_USER,
        };
      }
      case 'campaign': {
        return {
          permission: PERMISSIONS.CHANGE_CAMPAIGN,
          ruleSet: {
            campaign: [CAMPAIGN_MANAGER],
            agency: [AGENCY_MANAGER],
            all: [ACCOUNT_MANAGER, MULTI_ACCOUNT_MANAGER],
          },
        };
      }
      default:
        return true;
    }
  }, [editText, isAzAccount]);

  // eslint-disable-next-line consistent-return
  const handleEditClick = () => {
    if (usersAction) {
      return usersAction(original.id);
    }
    if (agenciesAction) {
      return agenciesAction(original.id);
    }
    if (suspensionAction) {
      return suspensionAction(original.suspensionItem);
    }
    history.push(path);
  };

  const handleSurveyClick = () => {
    setOpenSurveyModal(true, original.id);
  };

  const entityCampaign = original?.campaign?.data ? original?.campaign : { data: { id: original?.campaign?.id } };
  const entityData = {
    secGeog: Array.isArray(original.sec_geogs) ? original.sec_geogs : original.sec_geogs?.data[0]?.id,
    agency: original.agency,
    agencyId: original.agencyId,
    campaign: entityCampaign,
    campaign_manager: original.campaign_manager,
    data_collector: original.dataCollectors,
    campaignSet: original.campaignSet,
    user: original.user,
  };

  const roleRuleSetSurvey = {
    secGeogs: [AGENCY_MANAGER, ACCOUNT_MANAGER, DATA_COLLECTOR, CAMPAIGN_MANAGER],
    all: [MULTI_ACCOUNT_MANAGER],
  };

  const isNewUi = useNewUi();

  return isCheckBox ? (
    <AccessControl permission={editAccess.permission} data={entityData} roleRuleSet={editAccess.ruleSet}>
      <Checkbox name={name} value={value} onChange={handleCheckbox} />
    </AccessControl>
  ) : (
    <Styled.CellWrapper>
      {!onlyEdit && !original?.residential && (
        <AccessControl permission={PERMISSIONS.ALLOW_ANY_VISIT} data={entityData} roleRuleSet={roleRuleSetSurvey}>
          <ActionIcon
            isNewUi={isNewUi}
            Icon={FiFilePlus}
            legacySrc={ADD_SURVEY}
            label="Survey this store"
            onActivate={handleSurveyClick}
          />
        </AccessControl>
      )}
      <AccessControl permission={editAccess.permission} data={entityData} roleRuleSet={editAccess.ruleSet}>
        <ActionIcon
          isNewUi={isNewUi}
          Icon={FiEdit2}
          legacySrc={EDIT_PEN}
          label="Edit this item"
          onActivate={handleEditClick}
        />
      </AccessControl>
      <Styled.Title>{`Edit ${editText}`}</Styled.Title>
    </Styled.CellWrapper>
  );
};

ActionIcon.propTypes = {
  isNewUi: PropTypes.bool.isRequired,
  Icon: PropTypes.elementType.isRequired,
  legacySrc: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onActivate: PropTypes.func.isRequired,
};

ActionCell.propTypes = {
  original: PropTypes.object.isRequired,
  agenciesAction: PropTypes.func,
  usersAction: PropTypes.func,
  suspensionAction: PropTypes.func,
  setOpenSurveyModal: PropTypes.func,
  route: PropTypes.string.isRequired,
  editText: PropTypes.string,
  onlyEdit: PropTypes.bool,
  isCheckBox: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.bool,
  handleCheckbox: PropTypes.func,
};

ActionCell.defaultProps = {
  agenciesAction: null,
  usersAction: null,
  suspensionAction: null,
  setOpenSurveyModal: () => {},
  editText: '',
  onlyEdit: false,
  isCheckBox: false,
  name: '',
  value: false,
  handleCheckbox: () => {},
};

export default memo(ActionCell);
